import { useState } from 'react';
import { Button, Group, List, Modal, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { deleteObject, deleteRaw, objectDependencies, type DataObject } from '@/api/objects';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';
import { objectName } from './objectName';
import { useKindLabel } from './useKindLabel';

export interface DeleteObjectModalProps {
  object: DataObject | null;
  onClose: () => void;
  /** `raw` (staff): drop only the packed original once an unpacked copy exists. */
  mode?: 'object' | 'raw';
  /** Called instead of the freed-bytes toast when the record itself was removed. */
  onRemoved?: () => void;
}

/** Confirm dialog for deleting an object; dependencies are warnings, not guards (§7.4). */
export function DeleteObjectModal({ object, onClose, mode = 'object', onRemoved }: DeleteObjectModalProps) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation();
  const { bytes } = useFormat();
  const kindLabel = useKindLabel();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const removing = mode === 'object' && !!object?.removable;
  const deps = useQuery({
    queryKey: ['object-deps', object?.public_id],
    queryFn: () => objectDependencies(object!.public_id),
    enabled: !!object && !removing,
  });
  const del = useMutation({
    mutationFn: () => (mode === 'raw' ? deleteRaw(object!.public_id, reason) : deleteObject(object!.public_id, reason)),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['objects'] });
      void qc.invalidateQueries({ queryKey: ['object', object?.public_id] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project'] });
      if ('removed' in r && r.removed) {
        notifications.show({ color: 'forest', message: t('delete.removed') });
        onRemoved?.();
      } else {
        notifications.show({ color: 'forest', message: t('delete.freed', { bytes: bytes(r.bytes) }) });
      }
      setReason('');
      onClose();
    },
    onError: (err) => notifications.show({ color: 'red', message: errorMessage(err) }),
  });

  return (
    <Modal
      opened={!!object}
      onClose={onClose}
      title={removing ? t('delete.removeTitle') : mode === 'raw' ? t('delete.rawTitle') : t('delete.title')}
    >
      <Stack gap="md">
        <Text>{object && `${kindLabel(object.kind)} · ${objectName(object)}`}</Text>
        <Text fz={14}>{removing ? t('delete.removeConfirm') : mode === 'raw' ? t('delete.rawConfirm') : t('delete.confirm')}</Text>
        {deps.data && deps.data.length > 0 && (
          <Stack gap={4}>
            <Text fz={14} fw={600}>
              {t('delete.dependencies')}
            </Text>
            <List fz={14} c={brand.textMuted}>
              {deps.data.map((d, i) => (
                <List.Item key={i}>{t(`delete.dep.${d.kind}`, { object: d.object ?? '', defaultValue: d.kind })}</List.Item>
              ))}
            </List>
          </Stack>
        )}
        {!removing && <TextInput label={t('delete.reason')} value={reason} onChange={(e) => setReason(e.currentTarget.value)} />}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {tc('common.cancel')}
          </Button>
          <Button color="red" loading={del.isPending} onClick={() => del.mutate()}>
            {t('delete.action')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
